import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { desktop, isCapacitor } from "@/lib/platform";
import { logoutAndSignOut } from "@/lib/apiClient";
import { useFacilityContext } from "@/hooks/useFacilityContext";
import { useAuth } from "@/hooks/useAuth";
import { clearReauthCredentialOnSignOut } from "@livingsyncs/ui";

/**
 * US-176 Phase 0 — writes the marker that lets the NEXT sign-in report this
 * lock (sessionLockTelemetry.ts's reportReauthCompletionIfLocked), so the
 * story's own kill-criterion (median >= 4 locks per 8-hour shift) can be
 * evaluated against real data. iPad only (isCapacitor) — desktop shares this
 * guard but is a single-user device, not the shared-cart problem this
 * measures.
 *
 * Deliberately does NOT attempt a network call at lock time (simplified
 * 2026-09-12, see LESSONS-LEARNT). Two things were tried and both made this
 * worse, not better:
 *   1. A fire-and-forget POST here raced unawaited against `logoff()`'s own
 *      fully-awaited `logoutAndSignOut()` -> `window.location.href`
 *      navigation, which aborts any still-in-flight request — it lost that
 *      race every time (zero rows in `session_lock_events` after ~2 weeks
 *      of real device usage).
 *   2. Awaiting that same call (even bounded by a timeout) before calling
 *      `logoff()` broke auto-lock-on-backgrounding entirely: iOS suspends
 *      the webview's JS execution while backgrounded, exactly when this
 *      handler also fires (sessionLock.ts's onBackground), so the gating
 *      promise/timeout could go unresolved indefinitely.
 * The actual fix is simpler than either attempt: don't report from the one
 * moment that's structurally hostile to it. `localStorage.setItem` here is
 * synchronous and can't race or hang. The report itself happens later, at
 * the next successful sign-in — a plain in-SPA state update with no
 * document navigation involved, so a normal fire-and-forget POST there
 * actually survives (see reportReauthCompletionIfLocked).
 *
 * No current facility, or no resolvable employee email, means the marker
 * couldn't be attributed safely on a shared device, so it's skipped rather
 * than written with a placeholder identity — review fix (Critical): an
 * identity-less marker is exactly what let a different employee's later
 * sign-in silently consume a stale one.
 */
function writeLockMarker(
  facilityId: string | null,
  employeeEmail: string | null,
): void {
  if (!isCapacitor || !facilityId || !employeeEmail) return;
  try {
    localStorage.setItem(
      "ls.lastLockAt",
      JSON.stringify({ t: Date.now(), facilityId, employeeEmail }),
    );
  } catch {
    // Storage unavailable — best-effort, never block logoff.
  }
}

/**
 * US-013 S3 / AC11 + US-015 S3 / AC11 — native idle automatic logoff
 * (HIPAA §164.312(a)(2)(iii)).
 *
 * On the web this renders nothing (`desktop` is undefined). On the idle-lock or
 * hard-sign-out signal from whichever shell is running, it clears all cached
 * PHI from memory, signs out, and returns to /login.
 *
 * TWO SHELLS, ONE HANDLER. The signals arrive over the same bridge contract but
 * are produced very differently: Electron's main process owns its own timer and
 * pushes over IPC, while Capacitor has a single JS runtime, so the iPad's timer
 * has to run in this bundle — see `lib/native/sessionLock.ts`. Both converge on
 * `onLock`/`onSignOut`, so the response below is shared.
 *
 * NOTE: "lock" and "sign-out" currently both perform a full automatic logoff
 * (the unambiguously compliant behavior). A lighter "lock-and-resume-in-place"
 * overlay is a deferred UX refinement — Amplify v6 has no
 * verify-password-without-re-auth primitive, so a true lock would
 * re-authenticate under the hood anyway. On iPad that refinement is US-015's
 * Face ID resume, which additionally depends on AC10's Keychain token store.
 *
 * Renamed from `DesktopSessionGuard` in US-015: it governs iPad locking too,
 * and a name asserting otherwise is the kind that gets believed.
 */
export function NativeSessionGuard() {
  const queryClient = useQueryClient();
  const { currentFacilityId } = useFacilityContext();
  // Cached query read (staleTime 5min) — no extra network call. Only used
  // to bind the lock marker to an identity; never causes this component to
  // itself trigger an auth fetch.
  const { data: authContext } = useAuth();
  const employeeEmail = authContext?.email ?? null;

  useEffect(() => {
    if (!desktop) return;
    const ds = desktop; // non-null reference for the closures below

    // NOT a plain unconditional redirect, and NOT an unbounded await either —
    // two real bugs found in this exact spot, same day, back to back.
    //
    // Bug 1 (fixed first): `logoutAndSignOut()` used to open with
    // `await api.post("/auth/logout")` on an axios client with NO configured
    // timeout — after a real lock/unlock cycle that request can hang
    // indefinitely, and the old code awaited the whole function before
    // redirecting, so the redirect sometimes never ran at all.
    //
    // Bug 2 (found immediately after shipping bug 1's fix, via live device
    // testing showing "lock no longer signs out AT ALL", every time): making
    // logoff() fire logoutAndSignOut() and redirect in the SAME tick, with
    // zero wait, created a NEW race. logoutAndSignOut()'s signOut() call
    // (the one that actually invalidates Amplify's LOCAL session) never got a
    // chance to even START before the hard navigation below tore down the
    // whole JS context — so LoginPage.tsx's own `getCurrentUser()` check
    // still found a valid local session on the very next page load, and
    // bounced the "signed out" user straight back to "/". Confirmed live.
    //
    // Fix: race logoutAndSignOut() against a short, bounded timeout, and
    // redirect when EITHER settles. logoutAndSignOut() itself was reordered
    // (see apiClient.ts) so signOut() runs first, giving it priority to
    // actually complete within this window. This is NOT the same shape the
    // earlier "never gate iOS logoff on a promise" lesson warns against:
    // that was about an update-completion promise with no natural upper
    // bound; this is a fixed, short cap that still fires the redirect no
    // matter what, and — per sessionLock.ts's own note that iOS resumes
    // (not discards) a suspended timer on foreground — even a timer that
    // got suspended mid-lock still resolves promptly once the device is
    // unlocked, which is exactly when the redirect needs to happen anyway.
    const REDIRECT_GRACE_MS = 500;
    const logoff = () => {
      queryClient.clear(); // drop cached PHI from memory immediately
      const redirect = () => {
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      };
      const signedOut = logoutAndSignOut().catch(() => {
        // best-effort — the redirect happens regardless, from the race below
      });
      const grace = new Promise<void>((resolve) =>
        setTimeout(resolve, REDIRECT_GRACE_MS),
      );
      void Promise.race([signedOut, grace]).then(redirect);
    };

    const offLock = ds.onLock(() => {
      // US-176 Phase 0 — a hard 8h-ceiling sign-out is a different event
      // class (a fresh cold sign-in follows, not a resumed lock), so this
      // is deliberately only wired to onLock, not onSignOut below.
      //
      // logoff() must always run synchronously and unconditionally here —
      // writeLockMarker is a plain, synchronous localStorage write with no
      // async step to gate anything on (see its own comment for why lock
      // time is the wrong moment to attempt a network report at all).
      writeLockMarker(currentFacilityId, employeeEmail);
      void logoff();
    });
    const offSignOut = ds.onSignOut(() => {
      // Defense-in-depth (US-176 Phase 0 review cycle 3): a hard-ceiling
      // sign-out means, by this codebase's own definition, "a fresh cold
      // sign-in follows, not a resumed lock" — so any lock marker left
      // over from an earlier lock on THIS device is now stale and must
      // not survive to be misread as a real resume by the next sign-in.
      // sessionLockTelemetry.ts's own staleness ceiling is the primary
      // guard; this just clears the marker proactively, at the moment
      // the codebase itself calls the underlying session over.
      try {
        localStorage.removeItem("ls.lastLockAt");
      } catch {
        // Storage unavailable — non-fatal, the read-time ceiling still guards.
      }
      // US-176 Phase 2 — a hard-ceiling sign-out means the device's re-auth
      // credential must not survive to the next sign-in either (spec §4.2).
      // Fired in parallel with logoff(), never awaited: the same lesson this
      // file already learned the hard way for logoutAndSignOut() (never gate
      // iOS logoff on a promise) applies to this call too. It rides
      // logoff()'s own grace window on a best-effort basis; even if the
      // native Keychain clear doesn't finish in time, clearAllReauthProfiles()
      // inside it is a synchronous localStorage write that always completes,
      // so the cached-profile picker never resurrects a credential that
      // later turns out to be gone.
      void clearReauthCredentialOnSignOut();
      void logoff();
    });
    return () => {
      offLock();
      offSignOut();
    };
    // currentFacilityId/employeeEmail included so either changing re-
    // registers the listener with fresh values — otherwise the closure
    // would report against a stale facility or identity (US-176 Phase 0).
  }, [queryClient, currentFacilityId, employeeEmail]);

  // Start the iPad-side producer. Kept in its own effect so the subscriptions
  // above are already attached before anything can fire, and dynamically
  // imported so the Capacitor plugins and `@livingsyncs/mobile` code-split out
  // of the web and desktop bundles entirely (US-015 AC1).
  //
  // employeeEmail is a dependency — regression fix, 2026-09-12. sessionLock.ts's
  // `startNativeIdleLock` has a one-way `fired` latch, permanently blocking any
  // further "ls:lock"/"ls:signout" once the FIRST one fires, on the explicit
  // assumption ("there is nothing to re-arm") that a fresh instance starts on
  // the next sign-in. Nothing did: `NativeSessionGuard` mounts once at the app
  // root (`App.tsx`, outside any auth boundary), and this effect previously had
  // `[]` deps, so it ran exactly once per app LAUNCH, not once per SIGN-IN — the
  // very first lock or backgrounding of the whole process permanently spent the
  // latch, and every idle-lock after that (even across sign-out/sign-in) did
  // nothing until the app was force-quit and relaunched. Confirmed live: after
  // one successful auto-lock, every subsequent test silently failed regardless
  // of what NativeSessionGuard.tsx itself did. Depending on employeeEmail tears
  // down the old producer (and its spent latch) and starts a fresh one on every
  // sign-in/sign-out transition, actually delivering the re-arm the original
  // comment already assumed existed.
  useEffect(() => {
    if (!isCapacitor) return;
    let stop: (() => void) | undefined;
    let cancelled = false;

    void import("@/lib/native/sessionLock").then(({ startNativeIdleLock }) => {
      // The effect may have torn down while the chunk was in flight; starting
      // then would leak an interval and a listener set with no owner.
      if (cancelled) return;
      stop = startNativeIdleLock();
    });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [employeeEmail]);

  return null;
}
